// DEPLOY: /var/www/woody.wtf/frontend/src/pages/IndexPerformance.js
import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';
import PerformanceEngine from '../chart/PerformanceEngine';

const PERIODS = ['1W', '1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y'];

const pct = v => v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(2) + '%';
const PIE_COLORS = ['#7C3AED', '#059669', '#D97706', '#2563EB', '#DB2777',
  '#0891B2', '#65A30D', '#9333EA', '#EA580C', '#0D9488', '#B45309', '#4F46E5'];
const PREF_KEY = 'index-performance-settings';

export function isLeveragedOrInverse(etf) {
  if (etf.gearingType && etf.gearingType !== 'standard') return true;
  const name = etf.name || '';
  return /\b(?:bull|bear|inverse|leveraged|geared|ultrapro)\b|\b(?:[2-9]|\d{2,}|1\.\d+)\s*[x×]|\b[x×]\s*[2-9]\b|-\s*1\s*[x×]\b/i.test(name);
}

function readPreferences() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(PREF_KEY) || '{}');
    return { colors: saved.colors || {}, hidden: saved.hidden || {}, excludeGeared: !!saved.excludeGeared };
  } catch {
    return { colors: {}, hidden: {}, excludeGeared: false };
  }
}

function defaultColor(i) {
  const h = (i * 137.508) % 360, a = 0.68 * Math.min(0.42, 0.58);
  const channel = n => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (0.42 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
      .toString(16).padStart(2, '0');
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}

const validWeight = h => h.weight != null && Number.isFinite(Number(h.weight)) && Number(h.weight) > 0;

function topHoldings(holdings) {
  const sorted = holdings.filter(validWeight).sort((a, b) => Number(b.weight) - Number(a.weight));
  const result = sorted.slice(0, 10).map(h => ({
    name: h.holding_name || h.holding_symbol || 'Unnamed holding', weight: Number(h.weight)
  }));
  const remainder = sorted.slice(10).reduce((sum, h) => sum + Number(h.weight), 0);
  if (remainder > 0) result.push({ name: 'Other reported holdings', weight: remainder });
  return result;
}

function allocationBy(holdings, field) {
  if (!holdings.some(h => validWeight(h) && h[field])) return [];
  const totals = new Map();
  for (const h of holdings.filter(validWeight)) {
    const name = h[field] || 'Unclassified';
    totals.set(name, (totals.get(name) || 0) + Number(h.weight));
  }
  const ranked = [...totals].map(([name, weight]) => ({ name, weight }))
    .sort((a, b) => b.weight - a.weight);
  const other = ranked.slice(10).reduce((sum, h) => sum + h.weight, 0);
  return [...ranked.slice(0, 10), ...(other > 0 ? [{ name: 'Other categories', weight: other }] : [])];
}

function AllocationPie({ title, entries, empty }) {
  const total = entries.reduce((sum, e) => sum + e.weight, 0);
  let start = 0;
  const stops = entries.map((item, i) => {
    const end = start + item.weight / total * 100;
    const stop = `${PIE_COLORS[i % PIE_COLORS.length]} ${start}% ${end}%`;
    start = end;
    return stop;
  });
  return (
    <div className="index-allocation">
      <h3>{title}</h3>
      {total > 0 ? (
        <div className="index-allocation-content">
          <div className="index-pie" role="img" aria-label={`${title} pie chart`}
            style={{ background: `conic-gradient(${stops.join(', ')})` }} />
          <div className="index-pie-key">
            {entries.map((item, i) => (
              <div key={`${item.name}-${i}`}>
                <i style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span title={item.name}>{item.name}</span>
                <strong>{item.weight.toFixed(2)}%</strong>
              </div>
            ))}
          </div>
        </div>
      ) : <p className="index-muted">{empty}</p>}
    </div>
  );
}


export default function IndexPerformance() {
  const hostRef = useRef(null);
  const engineRef = useRef(null);
  const [mode, setMode] = useState('indexes');
  const [series, setSeries] = useState([]);
  const [ranked, setRanked] = useState([]);
  const [period, setPeriod] = useState('1Y');
  const [market, setMarket] = useState('ALL');
  const [pinned, setPinned] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState(readPreferences);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    const engine = new PerformanceEngine(hostRef.current, setRanked);
    engineRef.current = engine;
    return () => { engine.destroy(); engineRef.current = null; };
  }, []);


  useEffect(() => {
    try { window.localStorage.setItem(PREF_KEY, JSON.stringify(preferences)); } catch {}
  }, [preferences]);

  useEffect(() => {
    let alive = true;
    setError('');
    setDetail(null);
    setSeries([]);
    if (cacheRef.current[mode]) {
      setSeries(cacheRef.current[mode]);
      setLoading(false);
      return () => { alive = false; };
    }
    setLoading(true);
    api.get(mode === 'etfs' ? '/compare/etfs' : '/compare/indexes').then(r => {
      if (!alive) return;
      const items = (r.data.series || []).map((item, i) => ({
        key: item.ticker,
        name: item.name,
        exchange: item.exchange,
        gearingType: item.gearing_type,
        baseColor: defaultColor(i),
        color: defaultColor(i),
        points: item.points.map(([day, close]) => [Date.parse(day + 'T00:00:00Z'), Number(close)])
          .filter(([t, close]) => Number.isFinite(t) && Number.isFinite(close) && close > 0),
      }));
      cacheRef.current[mode] = items;
      setSeries(items);
      if (!items.some(item => item.points.length))
        setError(`No ${mode === 'etfs' ? 'ETF' : 'index'} history available.`);
    }).catch(() => { if (alive) setError(`Could not load ${mode === 'etfs' ? 'ETF' : 'index'} history.`); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mode]);

  useEffect(() => {
    const engine = engineRef.current;
    engine?.setData(series.filter(s => (market === 'ALL' || s.exchange === market) &&
        (mode !== 'etfs' || !preferences.excludeGeared || !isLeveragedOrInverse(s)))
      .map(s => ({
        ...s, color: preferences.colors[mode]?.[s.key] || s.baseColor,
        hidden: !!preferences.hidden[mode]?.[s.key]
      })));
  }, [series, market, mode, preferences.excludeGeared]);

  useEffect(() => {
    engineRef.current?.setAppearance(preferences.colors[mode] || {}, preferences.hidden[mode] || {});
  }, [preferences, mode]);

  useEffect(() => { engineRef.current?.setPeriod(period); }, [period]);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setDetailError('');
    setDetailLoading(false);
    if (mode !== 'etfs' || !pinned) return () => { alive = false; };
    const [symbol, exchange] = pinned.split(':');
    setDetailLoading(true);
    api.get(`/etf/${encodeURIComponent(symbol)}?exchange=${encodeURIComponent(exchange)}`)
      .then(r => { if (alive) setDetail(r.data.etf); })
      .catch(() => { if (alive) setDetailError('Could not load ETF details.'); })
      .finally(() => { if (alive) setDetailLoading(false); });
    return () => { alive = false; };
  }, [mode, pinned]);


  const changeMode = next => {
    setPinned(null);
    setActiveKey(null);
    engineRef.current?.focus(null);
    setMode(next);
  };

  const changeVisibility = (key, visible) => {
    setPreferences(p => ({
      ...p, hidden: { ...p.hidden, [mode]: { ...p.hidden[mode], [key]: !visible } }
    }));
    if (!visible && pinned === key) engineRef.current?.focus(null);
  };

  const changeColor = (key, color) => setPreferences(p => ({
    ...p, colors: { ...p.colors, [mode]: { ...p.colors[mode], [key]: color } }
  }));

  const toggleExcludeGeared = checked => {
    setPinned(null);
    setActiveKey(null);
    engineRef.current?.focus(null);
    setPreferences(p => ({ ...p, excludeGeared: checked }));
  };

  const changeMarket = next => {

    setPinned(null);
    setActiveKey(null);
    engineRef.current?.focus(null);
    setMarket(next);
  };

  const highlight = key => {
    setActiveKey(key);
    engineRef.current?.focus(key);
  };
  const pin = key => {
    const next = pinned === key ? null : key;
    setPinned(next);
    highlight(next);
  };


  const selectedIndex = ranked.find(s => s.key === activeKey) || ranked[0];
  const selectedDetail = ranked.find(s => s.key === pinned);
  const holdings = detail?.holdings || [];
  const positiveHoldings = holdings.filter(validWeight);
  const biggest = [...positiveHoldings].sort((a, b) => Number(b.weight) - Number(a.weight)).slice(0, 10);
  const lastClose = selectedDetail?.points.find(([t]) => t === selectedDetail.lastDate)?.[1];


  return (
    <div className="index-performance-page">
      <Navbar compact />
      <main className="index-performance-content">
        
        <h1>Index & ETF Performance</h1>
        <div className="index-mode" role="group" aria-label="Instrument type">
          <button type="button" className={mode === 'indexes' ? 'active' : ''}
            aria-pressed={mode === 'indexes'} onClick={() => changeMode('indexes')}>Indexes</button>
          <button type="button" className={mode === 'etfs' ? 'active' : ''}
            aria-pressed={mode === 'etfs'} onClick={() => changeMode('etfs')}>ETFs</button>
        </div>

        <div className="index-periods">
          {PERIODS.map(p => (
            <button type="button" key={p} className={period === p ? 'active' : ''}
              onClick={() => setPeriod(p)}>{p}</button>
          ))}
          <div className="index-markets" role="group" aria-label="Market">
            {['ALL', 'AU', 'US'].map(m => (
              <button type="button" key={m} className={market === m ? 'active' : ''}
                aria-pressed={market === m} onClick={() => changeMarket(m)}>
                {m === 'ALL' ? 'All' : m}
              </button>
            ))}
          </div>
          {mode === 'etfs' && (
            <label className="index-exclude-geared">
              <input type="checkbox" checked={preferences.excludeGeared}
                onChange={e => toggleExcludeGeared(e.target.checked)} />
              Exclude leveraged / inverse
            </label>
          )}
          <span>Drag to pan · scroll or pinch to zoom · double-click to reset</span>
        </div>
        {selectedIndex && (
          <div className="index-description">
            <span className="index-description-dot" style={{ background: selectedIndex.color }} />
            <strong>{selectedIndex.key}</strong>
            <span>{selectedIndex.name}</span>
          </div>
        )}
        {error && <p className="index-error">{error}</p>}
        <div className="index-performance-grid">
          <div className="index-canvas-wrap" ref={hostRef}>
            {loading && <div className="index-loading">Loading {mode === 'etfs' ? 'ETFs' : 'indexes'}…</div>}
          </div>
          <aside className="index-ranking">
            <div className="index-ranking-head">{mode === 'etfs' ? 'ETF' : 'Index'} <span>Return</span></div>
            <div className="index-ranking-list">
              {ranked.map(s => (
                <div key={s.key}
                  className={`index-ranking-row ${pinned === s.key ? 'pinned' : ''} ${preferences.hidden[mode]?.[s.key] ? 'index-hidden' : ''}`}>
                  <input type="checkbox" className="index-rank-check" aria-label={`Show ${s.key}`}
                    checked={!preferences.hidden[mode]?.[s.key]}
                    onClick={e => e.stopPropagation()}
                    onChange={e => changeVisibility(s.key, e.target.checked)} />
                  <input type="color" className="index-rank-color" aria-label={`Colour for ${s.key}`}
                    value={s.color} onClick={e => e.stopPropagation()}
                    onChange={e => changeColor(s.key, e.target.value)} />
                  <button type="button" className="index-rank-main"
                    onMouseEnter={() => highlight(s.key)}
                    onMouseLeave={() => highlight(pinned)}
                    onFocus={() => highlight(s.key)}
                    onBlur={() => highlight(pinned)}
                    onClick={() => pin(s.key)}
                    title={`${s.name} · ${s.key}${s.lastDate ? ' · last: ' + new Date(s.lastDate).toLocaleDateString('en-AU', { timeZone: 'UTC' }) : ''}`}>
                  <span className="index-rank-info">
                    <strong>{s.key}</strong>
                    <small>{s.name}</small>
                  </span>
                  <span className="index-rank-pct" style={{ color: s.pct == null ? 'var(--text-muted)' : s.pct >= 0 ? 'var(--success)' : 'var(--error)' }}>
                    {pct(s.pct)}
                  </span>
                  </button>
                </div>
              ))}
            </div>
          </aside>
        </div>
        {selectedDetail && (
          <section className="index-detail">
            <h2>{selectedDetail.key} <span>{selectedDetail.name}</span></h2>
            <div className="index-facts">
              <div><small>Market</small><strong>{selectedDetail.exchange}</strong></div>
              <div><small>Period return</small><strong>{pct(selectedDetail.pct)}</strong></div>
              <div><small>Last adjusted close</small><strong>{lastClose != null ? Number(lastClose).toLocaleString('en-AU', { maximumFractionDigits: 4 }) : '—'}</strong></div>
              <div><small>Last session</small><strong>{selectedDetail.lastDate ? new Date(selectedDetail.lastDate).toLocaleDateString('en-AU', { timeZone: 'UTC' }) : '—'}</strong></div>
            </div>
            {mode === 'etfs' && (
              <>
                {detailLoading && <p className="index-muted">Loading fund details…</p>}
                {detailError && <p className="index-error">{detailError}</p>}
                {detail && (
                  <>
                    {detail.description && <p className="index-fund-description">{detail.description}</p>}
                    {detail.notes && <p className="index-fund-description">{detail.notes}</p>}
                    <div className="index-facts">
                      {[
                        ['Provider', detail.provider_name || detail.fund_provider],
                        ['Index tracked', detail.index_tracked], ['Generic index', detail.generic_index],
                        ['ISIN', detail.isin], ['Product type', detail.product_type],
                        ['Asset class', detail.asset_class], ['Investment universe', detail.investment_universe],
                        ['Base currency', detail.base_currency], ['Structure', detail.fund_structure],
                        ['Inception', detail.inception_date], ['Distribution', detail.dividend_frequency],
                        ['Expense ratio', detail.expense_ratio == null ? null : `${Number(detail.expense_ratio).toFixed(2)}%`],
                        ['AUM (USD)', detail.aum_usd == null ? null : Number(detail.aum_usd).toLocaleString('en-AU')],
                        ['TTM yield', detail.ttm_yield == null ? null : `${Number(detail.ttm_yield).toFixed(2)}%`],
                        ['Geared', detail.is_geared ? `${detail.gearing_ratio || '—'}x` : 'No'],
                        ['Hedged', detail.currency_hedged == null ? null : String(detail.currency_hedged)],
                        ['Management', detail.actively_managed == null ? null : String(detail.actively_managed) === 'true' ? 'Active' : 'Passive'],
                        ['Holdings updated', detail.last_fetched_at ? new Date(detail.last_fetched_at).toLocaleDateString('en-AU') : null],
                        ['Fundamentals updated', detail.fundamentals_fetched_at ? new Date(detail.fundamentals_fetched_at).toLocaleDateString('en-AU') : null],
                        ['Fundamentals source', detail.fundamentals_source],
                      ].filter(([, value]) => value != null && value !== '').map(([label, value]) => (
                        <div key={label}><small>{label}</small><strong>{value}</strong></div>
                      ))}
                    </div>
                    <div className="index-allocations">
                      <AllocationPie title="Top 10 holdings by weight" entries={topHoldings(holdings)}
                        empty="No positive holding weights stored." />
                      <AllocationPie title="Sector exposure" entries={allocationBy(holdings, 'sector')}
                        empty="No sector allocation stored for these holdings." />
                      <AllocationPie title="Geographic exposure by country" entries={allocationBy(holdings, 'country_code')}
                        empty="No holding-country allocation stored." />
                    </div>
                    <p className="index-muted">Pies use positive recorded holding weights only. Unclassified holdings are shown where partial classifications exist; negative exposures and unreported holdings are not estimated. Nested funds are not looked through.</p>
                    {biggest.length > 0 && (
                      <div className="index-holdings">
                        <h3>Top 10 holdings</h3>
                        <table>
                          <thead><tr><th>Holding</th><th>Symbol</th><th>Country</th><th>Weight</th></tr></thead>
                          <tbody>{biggest.map(h => (
                            <tr key={h.holding_id}>
                              <td>{h.holding_name || '—'}</td>
                              <td>{h.holding_symbol || '—'}</td>
                              <td>{h.country_code || '—'}</td>
                              <td>{Number(h.weight).toFixed(2)}%</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    <a className="index-workspace-link" href={`/etf/workspace?symbol=${encodeURIComponent(detail.symbol)}&exchange=${encodeURIComponent(detail.exchange)}`}>Open holdings workspace</a>
                  </>
                )}
              </>
            )}
          </section>
        )}
        <p className="index-note index-footnote">Returns are split-adjusted price returns, not dividend-reinvested total returns. Missing trading days are not filled. Uncheck instruments to hide their lines, or choose colours to avoid conflicts. Click a row for details.</p>
      </main>
      <style>{`

        .index-performance-page { min-height:100vh; background:var(--bg); color:var(--text); }
        .index-mode { display:flex; gap:5px; margin:10px 0 14px; }
        .index-mode button { border:1px solid var(--border); background:var(--surface); color:var(--text-muted);
          border-radius:5px; padding:8px 16px; font:700 12px 'DM Mono',monospace; cursor:pointer; }
        .index-mode button.active { color:#fff; border-color:var(--accent); background:var(--accent); }

        .index-performance-content { margin:0 auto; max-width:1600px; padding:24px 20px; }
        .index-performance-content h1 { font:700 22px 'DM Sans',sans-serif; margin:0 0 6px; }
        .index-note { font:12px 'DM Sans',sans-serif; color:var(--text-muted); margin:0 0 18px; }
        .index-footnote { margin:12px 0; }
        .index-description { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin:0 0 10px;
          font:13px 'DM Sans',sans-serif; color:var(--text); }
        .index-description strong { font:700 12px 'DM Mono',monospace; }
        .index-description-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
        .index-periods { display:flex; align-items:center; gap:5px; flex-wrap:wrap; margin-bottom:14px; }
        .index-periods button { border:1px solid var(--border); background:var(--surface); color:var(--text-muted);
          font:600 12px 'DM Mono',monospace; border-radius:5px; padding:7px 11px; cursor:pointer; }
        .index-periods button.active { background:var(--accent); color:#fff; border-color:var(--accent); }
        .index-markets { display:flex; gap:5px; margin-left:auto; }
        .index-exclude-geared { display:flex; align-items:center; gap:5px; white-space:nowrap;
          color:var(--text); font:12px 'DM Sans',sans-serif; cursor:pointer; }
        .index-exclude-geared input { margin:0; accent-color:var(--accent); cursor:pointer; }
        .index-periods span { font:11px 'DM Sans',sans-serif; color:var(--text-muted); margin-left:12px; }
        .index-performance-grid { display:grid; grid-template-columns:minmax(0,1fr) 250px; gap:8px; height: min(68vh, 620px); min-height:380px; }
        .index-canvas-wrap { position:relative; min-width:0; height:100%; border:1px solid var(--border); background:var(--surface); border-radius:6px; overflow:hidden; }
        .index-loading { position:absolute; inset:0; display:grid; place-items:center; font:13px 'DM Sans',sans-serif; color:var(--text-muted); pointer-events:none; }
        .index-ranking { display:flex; flex-direction:column; min-height:0; border:1px solid var(--border); border-radius:6px; background:var(--surface); }
        .index-ranking-head { display:flex; justify-content:space-between; padding:12px 10px 8px; border-bottom:1px solid var(--border);
          font:700 11px 'DM Mono',monospace; color:var(--text-muted); }
        .index-ranking-list { min-height:0; overflow-y:auto; overscroll-behavior:contain; }
        .index-ranking-row { display:flex; align-items:center; width:100%; gap:7px; text-align:left; padding:7px 8px;
          cursor:pointer; border:0; border-bottom:1px solid var(--border); background:transparent; color:var(--text); }
        .index-ranking-row:hover, .index-ranking-row.pinned { background:var(--accent-dim); }
        .index-rank-main { flex:1; min-width:0; display:flex; align-items:center; gap:7px; text-align:left; border:0;
          background:transparent; color:var(--text); cursor:pointer; padding:0; font:inherit; }
        .index-rank-main:focus-visible { outline:1px solid var(--accent); }

        .index-rank-check { width:14px; height:14px; flex-shrink:0; accent-color:var(--accent); cursor:pointer; }
        .index-rank-color { flex-shrink:0; width:21px; height:22px; padding:0; border:0; background:transparent; cursor:pointer; }
        .index-ranking-row.index-hidden .index-rank-info, .index-ranking-row.index-hidden .index-rank-pct { opacity:0.35; }

        .index-rank-info { flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
        .index-rank-info strong { font:700 11px 'DM Mono',monospace; }
        .index-rank-info small { font:11px 'DM Sans',sans-serif; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; color:var(--text-muted); }
        .index-rank-pct { font:600 11px 'DM Mono',monospace; white-space:nowrap; }

        .index-error { font:12px 'DM Sans',sans-serif; color:var(--error); }
        .index-detail { border:1px solid var(--border); background:var(--surface); border-radius:6px;
          padding:16px; margin-top:14px; font:12px 'DM Sans',sans-serif; }
        .index-detail h2 { font:700 16px 'DM Mono',monospace; margin:0 0 14px; }
        .index-detail h2 span { font:400 13px 'DM Sans',sans-serif; color:var(--text-muted); }
        .index-facts { display:flex; flex-wrap:wrap; gap:10px 28px; margin:0 0 14px; }
        .index-facts div { min-width:110px; max-width:240px; display:flex; flex-direction:column; gap:4px; }
        .index-facts small { color:var(--text-muted); font:10px 'DM Mono',monospace; }
        .index-facts strong { overflow-wrap:anywhere; font:600 12px 'DM Sans',sans-serif; }
        .index-fund-description { color:var(--text); line-height:1.6; max-width:960px; white-space:pre-line; }
        .index-allocations { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:12px; margin:15px 0; }
        .index-allocation { border:1px solid var(--border); border-radius:5px; padding:12px; min-width:0; }
        .index-allocation h3, .index-holdings h3 { margin:0 0 12px; font:700 12px 'DM Sans',sans-serif; }
        .index-allocation-content { display:flex; align-items:flex-start; gap:14px; flex-wrap:wrap; }
        .index-pie { width:112px; height:112px; border-radius:50%; flex-shrink:0; }
        .index-pie-key { flex:1; min-width:130px; }
        .index-pie-key > div { display:flex; align-items:baseline; gap:6px; margin-bottom:6px; min-width:0; font:10px 'DM Sans',sans-serif; }
        .index-pie-key i { width:8px; height:8px; display:inline-block; flex-shrink:0; border-radius:2px; }
        .index-pie-key span { flex:1; overflow-wrap:anywhere; }
        .index-pie-key strong { white-space:nowrap; font:600 10px 'DM Mono',monospace; }
        .index-muted { color:var(--text-muted); font:11px 'DM Sans',sans-serif; }
        .index-holdings { margin:15px 0; overflow-x:auto; }
        .index-holdings table { width:100%; border-collapse:collapse; }
        .index-holdings th, .index-holdings td { padding:7px 10px; text-align:left; border-bottom:1px solid var(--border); }
        .index-holdings th { color:var(--text-muted); font:700 10px 'DM Mono',monospace; }
        .index-holdings td:last-child, .index-holdings th:last-child { text-align:right; }
        .index-workspace-link { display:inline-block; margin-top:4px; color:var(--accent); font-weight:700; }
        @media (max-width:1000px) { .index-allocations { grid-template-columns:1fr; } }

        @media (max-width:700px) {
          .index-performance-content { padding:16px 8px; }
          .index-performance-content h1 { font-size:18px; }
          .index-markets { margin-left:0; }
          .index-periods span { width:100%; margin:4px 0 0; }
          .index-performance-grid { grid-template-columns:minmax(0,1fr) 148px; gap:3px; height:65vh; min-height:380px; }
          .index-ranking-head { padding:9px 4px; }

          .index-ranking-row { gap:3px; padding:7px 3px; flex-wrap:wrap; }
          .index-rank-color { width:17px; height:18px; }
          .index-rank-check { width:12px; height:12px; }

          .index-rank-info strong { font-size:10px; }
          .index-rank-info small { display:none; }
          .index-rank-pct { font-size:10px; margin-left:auto; }
        }
      `}</style>
    </div>
  );
}
